import React from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {Link as RouterLink, generatePath} from 'react-router';

import {NotificationButton} from '@edna/components';
import {Icon} from '@edna/components/primitives';
import HelpIcon from '@edna/icons/help.svg';

import {useNavigateToReportByBroadcastId} from 'src/containers/Report/hooks';
import {useDownloadFile} from 'src/containers/ServerFile';
import {useGetCurrentUserQuery} from 'src/containers/User';
import {useEdnaLinks} from 'src/hooks';
import {AudienceIcon, DownloadIcon, SendIcon} from 'src/icons';
import PATHS from 'src/paths';
import {parseISO} from 'src/utils';

import {
  EAutopaymentNotificationStatus,
  EWebNotificationType,
  NOTIFICATIONS_LIST_PATH,
  TBroadcastNotification,
  TChannelProfileDisableNotification,
  TChannelProfileMarketingMessagesApiTermsSignedNotification,
  TChannelProfileMaxBotRegistrationNotification,
  TChannelProfileMaxBotSubjectCreatedNotification,
  TChannelProfileMaxConnectedNotification,
  TChannelProfileMaxDisconnectedNotification,
  TChannelProfileMessagingLimitChangedNotification,
  TChannelProfilePartnerProfileCreated,
  TChannelProfileQualityScoreChangedNotification,
  TChannelProfileQualityStatusChangedNotification,
  TChannelProfileReinstateNotification,
  TChannelProfileRestrictionNotification,
  TChannelProfileTelegramConnectedNotification,
  TChannelProfileTelegramDisconnectedNotification,
  TChannelProfileViolationNotification,
  TCompanyDetailsUpdatedNotification,
  TFlowMessageReportNotification,
  TJivoNotification,
  TLicenseExpiringNotification,
  TMessageMatcherCategoryChangedNotification,
  TMessageMatcherStatusChangedNotification,
  TMessageReportNotification,
  TPayKeeperPaymentNotification,
  TPaymentDetailsUpdatedNotification,
  TStripeAutopaymentNotification,
  TSubscribersUploadNotification,
  TTenantTariffAcceptedNotification,
  TWebNotification,
  UPLOADS_LIST_PATH,
  UPLOADS_TYPES,
} from '../model';
import * as S from '../style';

type TProps = {
  item: TWebNotification;
};

type TCommonNotificationProps = {
  type: EWebNotificationType;
  values?: TAnyObject;
  children?: React.ReactNode;
  actions?: React.ReactNode;
};

const NotificationsLink = React.memo(() => {
  const {t} = useTranslation();

  return (
    <S.Link hasLeftMargin to={NOTIFICATIONS_LIST_PATH} as={RouterLink}>
      {t('Notifications:toNotifications')}
    </S.Link>
  );
});

const UploadsLink = React.memo<{hasActions?: boolean}>(({hasActions}) => {
  const {t} = useTranslation();

  // может быть только 1 кнопка в уведомлении
  if (hasActions) {
    return (
      <S.Link hasLeftMargin to={UPLOADS_LIST_PATH} as={RouterLink}>
        {t('Notifications:toUploads')}
      </S.Link>
    );
  }

  return (
    <NotificationButton to={UPLOADS_LIST_PATH} as={RouterLink} hasMarginLeft>
      <Icon as={S.MoreHorizIcon} />
      {t('Notifications:toUploads')}
    </NotificationButton>
  );
});

const CommonNotification = React.memo<TCommonNotificationProps>(
  ({values, type, children, actions}) => (
    <>
      <Trans i18nKey={`Notifications:web.${type}`} values={values}>
        {children}
      </Trans>
      {UPLOADS_TYPES.includes(type) ? (
        <UploadsLink hasActions={!!actions} />
      ) : (
        <NotificationsLink />
      )}
      {actions}
    </>
  ),
);

const SubscribersUpload = React.memo<{item: TSubscribersUploadNotification}>(
  ({item: {meta, type}}) => {
    const {t} = useTranslation();
    const subscriberUpload = meta?.subscriberUpload;
    const downloadFile = useDownloadFile();
    const links = useEdnaLinks();

    const handleDownloadFile = () => {
      if (!subscriberUpload?.errorReportFileName) {
        return;
      }

      downloadFile(subscriberUpload.errorReportFileName, subscriberUpload.fileName);
    };

    let button = null;

    if (type === EWebNotificationType.SubscribersUploadFailed) {
      button = (
        <NotificationButton as="a" href={`mailto:${links.SUPPORT_EMAIL}`} hasMarginLeft>
          <Icon as={HelpIcon} />
          {t('Notifications:button.contactTechnicalSupport')}
        </NotificationButton>
      );
    }

    if (type === EWebNotificationType.SubscribersUploadPartial) {
      button = (
        <NotificationButton onClick={handleDownloadFile} hasMarginLeft>
          <Icon as={DownloadIcon} />
          {t('Notifications:button.downloadErrorFile')}
        </NotificationButton>
      );
    }

    if (type === EWebNotificationType.SubscribersUploadSuccess) {
      button = (
        <NotificationButton as={RouterLink} to={PATHS.AUDIENCE} hasMarginLeft>
          <Icon as={AudienceIcon} />
          {t('Notifications:button.toAudienceSection')}
        </NotificationButton>
      );
    }

    return <CommonNotification type={type} actions={button} />;
  },
);

const Broadcast = React.memo<{item: TBroadcastNotification}>(
  ({
    item: {
      meta: {broadcast},
      type,
    },
  }) => {
    const {t} = useTranslation();

    const goToReport = useNavigateToReportByBroadcastId({broadcastId: broadcast?.id});

    return (
      <CommonNotification
        type={type}
        values={broadcast}
        actions={
          <NotificationButton hasMarginLeft onClick={goToReport}>
            <Icon as={AudienceIcon} />
            {t('Notifications:button.toReportBroadcast')}
          </NotificationButton>
        }
      />
    );
  },
);

const License = React.memo<{item: TLicenseExpiringNotification}>(
  ({
    item: {
      type,
      meta: {licenseExpiring},
    },
  }) => {
    if (type === EWebNotificationType.LicenseExpiring && licenseExpiring !== undefined) {
      const date = parseISO(licenseExpiring.expireAt, 'dd.MM.yyyy');

      return <Trans i18nKey={`Notifications:web.${type}`} values={{date}} />;
    }

    return null;
  },
);

const TenantDetailsUpdates = React.memo<{
  item: TCompanyDetailsUpdatedNotification | TPaymentDetailsUpdatedNotification;
}>(({item}) => {
  const login =
    item.type === EWebNotificationType.CompanyDetailsUpdated
      ? item.meta.updateCompanyDetails.login
      : item.meta.updatePaymentDetails.login;

  return <CommonNotification type={item.type} values={{login}} />;
});

const TenantAcceptance = React.memo<{item: TTenantTariffAcceptedNotification}>(({item}) => (
  <CommonNotification type={item.type} values={{login: item.meta.acceptTenantTariff.login}} />
));

const Jivo = React.memo<{item: TJivoNotification}>(({item}) => (
  <CommonNotification type={item.type} values={{login: item.meta?.updateJivoIntegration?.login}} />
));

const MessageMatcher = React.memo<{
  item: TMessageMatcherStatusChangedNotification | TMessageMatcherCategoryChangedNotification;
}>(({item}) => {
  const {t} = useTranslation();

  if (item.type === EWebNotificationType.MessageMatcherStatusChanged) {
    const {name, approveStatus} = item.meta?.messageMatcher;

    return (
      <>
        {t(`Notifications:web.${item.type}.${approveStatus}`, {name})}
        <NotificationsLink />
      </>
    );
  }

  if (item.type === EWebNotificationType.MessageMatcherCategoryChanged) {
    const {name, newCategory} = item.meta?.messageMatcherCategoryChanged;

    return (
      <CommonNotification
        type={item.type}
        values={{
          name,
          newCategory: t(`MessageMatchers:editForm.categories.${newCategory}`),
        }}
      />
    );
  }

  return null;
});

const StripeAutopayment = React.memo<{item: TStripeAutopaymentNotification}>(
  ({item: {meta, type}}) => {
    const links = useEdnaLinks();

    if (!meta?.checkoutData) {
      return null;
    }

    if (meta.checkoutData.status === EAutopaymentNotificationStatus.PAYMENT_FAILED) {
      return (
        <Trans
          i18nKey={`Notifications:web.${type}.${meta.checkoutData.declineCode}`}
          values={{
            date: parseISO(meta.checkoutData.occurredAt, 'dd.MM.yyyy'),
            email: links.SUPPORT_EMAIL,
          }}
        >
          <S.Link href={`mailto:${links.SUPPORT_EMAIL}`} />
        </Trans>
      );
    }

    return (
      <Trans i18nKey={`Notifications:web.${type}.${meta.checkoutData.status}`}>
        <S.Link href={meta.checkoutData.hostedReceiptUrl} target="_blank" />
      </Trans>
    );
  },
);

const ChannelProfileQuality = React.memo<{
  item:
    | TChannelProfileQualityScoreChangedNotification
    | TChannelProfileMessagingLimitChangedNotification
    | TChannelProfileQualityStatusChangedNotification;
}>(({item}) => {
  const {t} = useTranslation();

  let values = {};

  if (item.type === EWebNotificationType.ChannelProfileQualityScoreChanged) {
    const {name, qualityScore} = item.meta.whatsAppProfileQualityScoreChanged;

    values = {name, qualityScore: t(`Channels:qualityScore.${qualityScore}`).toLowerCase()};
  }

  if (item.type === EWebNotificationType.ChannelProfileMessagingLimitChanged) {
    const {name, messagingLimit} = item.meta.whatsAppProfileMessagingLimitChanged;

    values = {name, messagingLimit: t(`Channels:limit.${messagingLimit}`).toLowerCase()};
  }

  if (item.type === EWebNotificationType.ChannelProfileQualityStatusChanged) {
    const {name, qualityStatus} = item.meta.whatsAppProfileQualityStatusChanged;

    values = {name, qualityStatus: t(`Channels:qualityStatus.${qualityStatus}`).toLowerCase()};
  }

  return <CommonNotification type={item.type} values={values} />;
});

const WhatsAppChannelProfile = React.memo<{
  item:
    | TChannelProfilePartnerProfileCreated
    | TChannelProfileDisableNotification
    | TChannelProfileReinstateNotification
    | TChannelProfileRestrictionNotification
    | TChannelProfileViolationNotification;
}>(({item}) => {
  const links = useEdnaLinks();

  let values = {};
  let link = null;

  if (item.type === EWebNotificationType.ChannelProfileWABAViolation) {
    const {whatsappEsProfileInternalNames, violationType, wabaId} =
      item.meta.whatsAppESProfileWABAViolation;

    values = {
      wabaId,
      channels: whatsappEsProfileInternalNames.join(', '),
      violationType,
    };
    link = links.WABA_BLOCK;
  }

  if (item.type === EWebNotificationType.ChannelProfileWABARestriction) {
    const {whatsappEsProfileInternalNames, expirationDate, wabaId} =
      item.meta.whatsAppESProfileWABARestriction;

    values = {
      wabaId,
      channels: whatsappEsProfileInternalNames.join(', '),
      expirationDate: parseISO(expirationDate, 'dd.MM.yyyy'),
    };
    link = links.WABA_BLOCK;
  }

  if (item.type === EWebNotificationType.ChannelProfileWABADisable) {
    const {whatsappEsProfileInternalNames, wabaId} = item.meta.whatsAppESProfileWABADisable;

    values = {wabaId, channels: whatsappEsProfileInternalNames.join(', ')};
    link = links.WABA_BLOCK;
  }

  if (item.type === EWebNotificationType.ChannelProfileWABAReinstate) {
    const {whatsappEsProfileInternalNames, wabaId} = item.meta.whatsAppESProfileWABAReinstate;

    values = {wabaId, channels: whatsappEsProfileInternalNames.join(', ')};
  }

  if (item.type === EWebNotificationType.ChannelProfilePartnerProfileCreated) {
    const {name} = item.meta;

    values = {name};
  }

  return (
    <CommonNotification type={item.type} values={values}>
      {!!link && <S.Link href={link} target="_blank" />}
    </CommonNotification>
  );
});

const MarketingMessagesApi = React.memo<{
  item: TChannelProfileMarketingMessagesApiTermsSignedNotification;
}>(({item}) => {
  const {t} = useTranslation();

  const channels =
    item.meta.whatsAppESProfileWABAMarketingMessagesApiTermsSigned.whatsappEsProfileInternalNames;

  return (
    <>
      {t(`Notifications:web.${item.type}.title`, {
        count: channels.length,
        channels: channels.join(', '),
      })}
    </>
  );
});

const MaxChannelProfile = React.memo<{
  item: TChannelProfileMaxConnectedNotification | TChannelProfileMaxDisconnectedNotification;
}>(({item}) => {
  const {t} = useTranslation();

  const meta =
    item.type === EWebNotificationType.ChannelProfileMaxConnected
      ? item.meta.maxConnected
      : item.meta.maxDisconnected;

  return (
    <CommonNotification
      type={item.type}
      values={{name: meta.profileName}}
      actions={
        <NotificationButton
          as={RouterLink}
          to={generatePath(PATHS.CHANNELS_MAX_VIEW, {id: String(meta.profileId)})}
          hasMarginLeft
        >
          <Icon as={SendIcon} />
          {t('Notifications:button.toChannel')}
        </NotificationButton>
      }
    />
  );
});

const TelegramChannelProfile = React.memo<{
  item:
    | TChannelProfileTelegramConnectedNotification
    | TChannelProfileTelegramDisconnectedNotification;
}>(({item}) => {
  const {t} = useTranslation();

  const meta =
    item.type === EWebNotificationType.ChannelProfileTelegramConnected
      ? item.meta.telegramConnected
      : item.meta.telegramDisconnected;

  return (
    <CommonNotification
      type={item.type}
      values={{name: meta.profileName}}
      actions={
        <NotificationButton
          as={RouterLink}
          to={generatePath(PATHS.CHANNELS_TELEGRAM_VIEW, {id: String(meta.profileId)})}
          hasMarginLeft
        >
          <Icon as={SendIcon} />
          {t('Notifications:button.toChannel')}
        </NotificationButton>
      }
    />
  );
});

const MaxBotChannelProfile = React.memo<{
  item:
    | TChannelProfileMaxBotRegistrationNotification
    | TChannelProfileMaxBotSubjectCreatedNotification;
}>(({item}) => {
  const {t} = useTranslation();

  const meta =
    item.type === EWebNotificationType.ChannelProfileMaxBotSubjectCreated
      ? item.meta.maxBotSubjectCreated
      : item.meta.maxBotRegistration;

  return (
    <CommonNotification
      type={item.type}
      values={{name: meta.profileName}}
      actions={
        <NotificationButton
          as={RouterLink}
          to={generatePath(PATHS.CHANNELS_MAX_BOT_VIEW, {id: String(meta.profileId)})}
          hasMarginLeft
        >
          <Icon as={SendIcon} />
          {t('Notifications:button.toChannel')}
        </NotificationButton>
      }
    />
  );
});

const MessageReport = React.memo<{
  item: TMessageReportNotification;
}>(({item}) => <CommonNotification type={item.type} values={item.meta.messageReport} />);

const FlowMessageReport = React.memo<{
  item: TFlowMessageReportNotification;
}>(({item}) => <CommonNotification type={item.type} values={item.meta.flowReport} />);

const PaykeeperPayment = React.memo<{item: TPayKeeperPaymentNotification}>(({item}) => {
  const {t} = useTranslation();
  const {data: user} = useGetCurrentUserQuery();

  return (
    <>
      {t(`Notifications:web.${item.type}.${item.meta.paymentData.status}`, {
        sum: item.meta.paymentData.sum,
        date: parseISO(item.meta.paymentData.paymentDate, 'dd.MM.yyyy'),
        login: user?.login,
      })}
    </>
  );
});

const InvoicePaid = React.memo<{item: TWebNotification}>(({item}) => {
  const {t} = useTranslation();

  return <>{t(`Notifications:web.${item.type}`)}</>;
});

const WebNotification = React.memo<TProps>(({item}) => {
  switch (item.type) {
    case EWebNotificationType.SubscribersUploadSuccess:
    case EWebNotificationType.SubscribersUploadFailed:
    case EWebNotificationType.SubscribersUploadPartial:
      return <SubscribersUpload item={item} />;

    case EWebNotificationType.MessageReportSuccess:
    case EWebNotificationType.MessageReportFailed:
      return <MessageReport item={item} />;

    case EWebNotificationType.FlowMessageReportSuccess:
    case EWebNotificationType.FlowMessageReportFailed:
      return <FlowMessageReport item={item} />;

    case EWebNotificationType.BroadcastCancelled:
    case EWebNotificationType.BroadcastCompleted:
      return <Broadcast item={item} />;

    case EWebNotificationType.LicenseExpiring:
      return <License item={item} />;

    case EWebNotificationType.CompanyDetailsUpdated:
    case EWebNotificationType.PaymentDetailsUpdated:
      return <TenantDetailsUpdates item={item} />;

    case EWebNotificationType.TenantTariffAccepted:
      return <TenantAcceptance item={item} />;

    case EWebNotificationType.JivoEnabled:
    case EWebNotificationType.JivoDisabled:
      return <Jivo item={item} />;

    case EWebNotificationType.MessageMatcherStatusChanged:
    case EWebNotificationType.MessageMatcherCategoryChanged:
      return <MessageMatcher item={item} />;

    case EWebNotificationType.StripeCheckout:
      return <StripeAutopayment item={item} />;

    case EWebNotificationType.ChannelProfileQualityScoreChanged:
    case EWebNotificationType.ChannelProfileMessagingLimitChanged:
    case EWebNotificationType.ChannelProfileQualityStatusChanged:
      return <ChannelProfileQuality item={item} />;

    case EWebNotificationType.ChannelProfileWABAViolation:
    case EWebNotificationType.ChannelProfileWABARestriction:
    case EWebNotificationType.ChannelProfileWABADisable:
    case EWebNotificationType.ChannelProfileWABAReinstate:
    case EWebNotificationType.ChannelProfilePartnerProfileCreated:
      return <WhatsAppChannelProfile item={item} />;

    case EWebNotificationType.ChannelProfileMaxBotRegistration:
    case EWebNotificationType.ChannelProfileMaxBotSubjectCreated:
      return <MaxBotChannelProfile item={item} />;

    case EWebNotificationType.ChannelProfileMaxDisconnected:
    case EWebNotificationType.ChannelProfileMaxConnected:
      return <MaxChannelProfile item={item} />;

    case EWebNotificationType.ChannelProfileTelegramConnected:
    case EWebNotificationType.ChannelProfileTelegramDisconnected:
      return <TelegramChannelProfile item={item} />;

    case EWebNotificationType.ChannelProfileWABAMarketingMessagesApiTermsSigned:
      return <MarketingMessagesApi item={item} />;

    case EWebNotificationType.PaykeeperPayment:
      return <PaykeeperPayment item={item} />;

    case EWebNotificationType.InvoicePaid:
      return <InvoicePaid item={item} />;

    case EWebNotificationType.CallbackUpdated:
    case EWebNotificationType.ApikeyUserUpdated:
      return <CommonNotification type={item.type} />;

    case EWebNotificationType.SubscribersUploadLoading:
    case EWebNotificationType.MessageReportLoading:
    case EWebNotificationType.FlowMessageReportLoading:
    case EWebNotificationType.AgreedChangeTariffRequest:
    case EWebNotificationType.NotAgreedChangeTariffRequest:
    default:
      return null;
  }
});

WebNotification.displayName = 'WebNotification';

export {WebNotification};
